// FR-AD7 operator mail: SMTP integration + editable templates.
// GET/PUT/DELETE /ops/mail/smtp, POST /ops/mail/test, GET/PUT /ops/mail/templates/{key}.
// The password field is write-only — GET returns `password_set`, never the secret.
//
// Why here and not the org admin console (FR-AD1): Grackle is a hosted service only (no
// self-host, CLAUDE.md / §1.2) and sends every product email from its own domain, so the SMTP
// provider is one platform-wide setting owned by staff. No FR gives orgs a custom sender.
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiFail } from "../api/client";
import type {
  EmailTemplateKey,
  EmailTemplateOut,
  ListResponse,
  OpsMailTestOut,
  OpsSmtpOut,
  OpsSmtpPut,
  SmtpTlsMode,
} from "../api/types";
import { Badge, Card, Select, Tabs, ToggleRow } from "@grackle/ui";
import { Button, Field, Notice } from "../components/ui";
import { DEFAULT_PORT, isEmail, validateSmtp, type SmtpFormErrors, type SmtpFormValues } from "./smtpForm";
import "../admin/admin.css";

const TLS_OPTIONS: { value: SmtpTlsMode; label: string }[] = [
  { value: "starttls", label: "STARTTLS (port 587)" },
  { value: "tls", label: "Implicit TLS (port 465)" },
  { value: "none", label: "None (no TLS)" },
];

const SAMPLE: Record<string, string> = {
  recipient_email: "ops@example.com",
  verify_url: "https://app.example.com/verify?token=example",
  join_url: "https://app.example.com/invite?token=example",
  reset_url: "https://app.example.com/reset-password?token=example",
  watch_url: "https://app.example.com/g/example",
  org_name: "Acme Design Co",
  inviter_name: "Wesley",
  sharer_name: "Priya N.",
  grackle_title: "Upload queue walkthrough",
  recorder_name: "Priya N.",
  app_name: "Grackle",
  code: "482913",
  notes_text: "Decided to ship the upload queue on Friday.",
  webhook_url_masked: "https://hooks.example.com/…/abcd",
  consecutive_failures: "20",
};

function renderPreview(input: string): string {
  return input.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (whole, name: string) => SAMPLE[name] ?? whole);
}

function Toggle({ on, onToggle, label, sub }: { on: boolean; onToggle: () => void; label: string; sub: string }) {
  return <ToggleRow label={label} description={sub} checked={on} onChange={() => onToggle()} />;
}

export default function OpsMailPage() {
  const [smtp, setSmtp] = useState<OpsSmtpOut | null>(null);
  const [templates, setTemplates] = useState<EmailTemplateOut[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [host, setHost] = useState("");
  const [port, setPort] = useState("587");
  const [tlsMode, setTlsMode] = useState<SmtpTlsMode>("starttls");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [clearPassword, setClearPassword] = useState(false);
  const [secretRef, setSecretRef] = useState("");
  const [fromAddress, setFromAddress] = useState("");
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [fieldErrors, setFieldErrors] = useState<SmtpFormErrors>({});

  const [savingSmtp, setSavingSmtp] = useState(false);
  const [smtpSaved, setSmtpSaved] = useState(false);
  const [smtpError, setSmtpError] = useState<string | null>(null);

  const [testTo, setTestTo] = useState("");
  const [testKey, setTestKey] = useState<EmailTemplateKey | "">("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const [activeKey, setActiveKey] = useState<string>("verify");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyPlain, setBodyPlain] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);
  const [tplSaved, setTplSaved] = useState(false);
  const [tplError, setTplError] = useState<string | null>(null);

  const applySmtp = (cfg: OpsSmtpOut) => {
    setSmtp(cfg);
    setEnabled(cfg.enabled);
    setHost(cfg.host ?? "");
    setPort(String(cfg.port ?? 587));
    setTlsMode((cfg.tls_mode as SmtpTlsMode) || "starttls");
    setUsername(cfg.username ?? "");
    setPassword("");
    setClearPassword(false);
    setSecretRef(cfg.password_secret_ref ?? "");
    setFromAddress(cfg.from_address ?? "");
    setFromName(cfg.from_name ?? "");
    setReplyTo(cfg.reply_to ?? "");
    setFieldErrors({});
  };

  const applyTemplate = (tpl: EmailTemplateOut) => {
    setActiveKey(tpl.key);
    setSubject(tpl.subject);
    setBodyHtml(tpl.body_html);
    setBodyPlain(tpl.body_plain);
    setTplSaved(false);
    setTplError(null);
  };

  const load = useCallback(() => {
    Promise.all([
      api<OpsSmtpOut>("GET", "/ops/mail/smtp"),
      api<ListResponse<EmailTemplateOut>>("GET", "/ops/mail/templates"),
    ])
      .then(([cfg, tpls]) => {
        applySmtp(cfg);
        setTemplates(tpls.items);
        const current = tpls.items.find((t) => t.key === activeKey) ?? tpls.items[0];
        if (current) applyTemplate(current);
      })
      .catch((err) => setLoadError(err instanceof ApiFail ? err.message : "Couldn't load mail settings."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const active = templates?.find((t) => t.key === activeKey) ?? null;

  function selectTemplate(key: string) {
    const tpl = templates?.find((t) => t.key === key);
    if (tpl) applyTemplate(tpl);
  }

  const formValues: SmtpFormValues = {
    host,
    port,
    tlsMode,
    username,
    password,
    secretRef,
    fromAddress,
    fromName,
    replyTo,
  };

  // Unsaved edits matter for the test card: POST /ops/mail/test sends through the *saved*
  // transport, so testing a half-edited form would silently test the old settings.
  const dirty =
    smtp !== null &&
    (enabled !== smtp.enabled ||
      host.trim() !== (smtp.host ?? "") ||
      port.trim() !== String(smtp.port ?? DEFAULT_PORT.starttls) ||
      tlsMode !== ((smtp.tls_mode as SmtpTlsMode) || "starttls") ||
      username.trim() !== (smtp.username ?? "") ||
      password.trim() !== "" ||
      clearPassword ||
      secretRef.trim() !== (smtp.password_secret_ref ?? "") ||
      fromAddress.trim() !== (smtp.from_address ?? "") ||
      fromName.trim() !== (smtp.from_name ?? "") ||
      replyTo.trim() !== (smtp.reply_to ?? ""));

  function onTlsChange(next: SmtpTlsMode) {
    // Follow the conventional port when the operator hasn't picked a custom one.
    if (port.trim() === "" || port.trim() === DEFAULT_PORT[tlsMode]) setPort(DEFAULT_PORT[next]);
    setTlsMode(next);
  }

  async function onSaveSmtp(e: FormEvent) {
    e.preventDefault();
    setSmtpError(null);
    setSmtpSaved(false);
    const errors = validateSmtp(formValues);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSmtpError("Fix the highlighted fields before saving.");
      return;
    }
    setSavingSmtp(true);
    const body: OpsSmtpPut = {
      enabled,
      host: host.trim(),
      port: Number(port.trim()),
      tls_mode: tlsMode,
      username: username.trim() || null,
      password: clearPassword ? undefined : password.trim() || undefined,
      clear_password: clearPassword || undefined,
      password_secret_ref: secretRef.trim() || null,
      from_address: fromAddress.trim(),
      from_name: fromName.trim() || null,
      reply_to: replyTo.trim() || null,
    };
    try {
      const cfg = await api<OpsSmtpOut>("PUT", "/ops/mail/smtp", body);
      applySmtp(cfg);
      setSmtpSaved(true);
    } catch (err) {
      setSmtpError(err instanceof ApiFail ? err.message : "Couldn't save SMTP settings.");
    } finally {
      setSavingSmtp(false);
    }
  }

  async function onClearSmtp() {
    setSavingSmtp(true);
    setSmtpError(null);
    try {
      const cfg = await api<OpsSmtpOut>("DELETE", "/ops/mail/smtp");
      applySmtp(cfg);
      setSmtpSaved(true);
    } catch (err) {
      setSmtpError(err instanceof ApiFail ? err.message : "Couldn't clear SMTP settings.");
    } finally {
      setSavingSmtp(false);
    }
  }

  async function onTestSend(e: FormEvent) {
    e.preventDefault();
    setTestError(null);
    setTestResult(null);
    const to = testTo.trim();
    if (!isEmail(to)) {
      setTestError("Enter a valid recipient address.");
      return;
    }
    setTesting(true);
    try {
      const res = await api<OpsMailTestOut>("POST", "/ops/mail/test", {
        to,
        template_key: testKey || null,
      });
      const via = res.transport === "console" ? "the saved SMTP settings" : "the GRACKLE_SMTP_URL fallback";
      setTestResult(`Test email “${res.subject}” sent to ${to} via ${via}. Check that inbox (and spam) to confirm delivery.`);
    } catch (err) {
      setTestError(`Test email failed: ${err instanceof ApiFail ? err.message : "the server did not respond."}`);
    } finally {
      setTesting(false);
    }
  }

  async function onSaveTemplate(e: FormEvent) {
    e.preventDefault();
    setSavingTpl(true);
    setTplError(null);
    setTplSaved(false);
    try {
      const saved = await api<EmailTemplateOut>("PUT", `/ops/mail/templates/${activeKey}`, {
        subject,
        body_html: bodyHtml,
        body_plain: bodyPlain,
      });
      setTemplates((prev) => (prev ? prev.map((t) => (t.key === saved.key ? saved : t)) : [saved]));
      applyTemplate(saved);
      setTplSaved(true);
    } catch (err) {
      setTplError(err instanceof ApiFail ? err.message : "Couldn't save that template.");
    } finally {
      setSavingTpl(false);
    }
  }

  async function onResetTemplate() {
    setSavingTpl(true);
    setTplError(null);
    try {
      const saved = await api<EmailTemplateOut>("POST", `/ops/mail/templates/${activeKey}/reset`);
      setTemplates((prev) => (prev ? prev.map((t) => (t.key === saved.key ? saved : t)) : [saved]));
      applyTemplate(saved);
      setTplSaved(true);
    } catch (err) {
      setTplError(err instanceof ApiFail ? err.message : "Couldn't reset that template.");
    } finally {
      setSavingTpl(false);
    }
  }

  const previewSubject = useMemo(() => renderPreview(subject), [subject]);
  const previewPlain = useMemo(() => renderPreview(bodyPlain), [bodyPlain]);

  const passwordHelp = smtp?.password_set
    ? "Leave blank to keep the stored password. It is never shown after save."
    : "Stored encrypted. Or set a secret ref to read the password from the environment at send time.";

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Email</h1>
          <p>SMTP integration and the templates Grackle sends. Secrets are write-only.</p>
        </div>
      </header>

      {loadError ? <Notice tone="error">{loadError}</Notice> : null}

      <Card size="sm" title="SMTP provider">
        <div className="cluster">
          {smtp?.configured && smtp.enabled ? (
            <Badge tone="success" size="sm">
              Active
            </Badge>
          ) : smtp?.configured ? (
            <Badge size="sm">Saved, not in use</Badge>
          ) : (
            <Badge size="sm">Not configured</Badge>
          )}
          <Badge size="sm">{smtp?.password_set ? "Password set" : "No stored password"}</Badge>
          {smtp?.updated_at ? <span className="gk-sm">Updated {new Date(smtp.updated_at).toLocaleString()}</span> : null}
        </div>
        <p className="gk-sm ops-card-note">
          Platform-wide: every org&apos;s verification, invite, share and notification email goes out through this
          provider.{" "}
          {smtp?.env_fallback
            ? "GRACKLE_SMTP_URL is set and is used when these settings are off or cleared."
            : "No GRACKLE_SMTP_URL fallback — with these settings off, links are only written to the server log."}
        </p>

        <form onSubmit={onSaveSmtp} className="ops-form" noValidate>
          <Toggle
            on={enabled}
            onToggle={() => setEnabled((v) => !v)}
            label="Use these settings"
            sub="When off, Grackle falls back to GRACKLE_SMTP_URL, then to logging the link."
          />

          <div className="admin-two-col">
            <Field
              label="Host"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="smtp.example.com"
              error={fieldErrors.host}
              required
            />
            <div className="gk-field">
              <Select id="ops-smtp-tls" label="Security" value={tlsMode} onChange={(v) => onTlsChange(v as SmtpTlsMode)} options={TLS_OPTIONS} />
              <div className="gk-field__help">None is for an internal relay only.</div>
            </div>
            <Field
              label="Port"
              className="mono"
              value={port}
              onChange={(e) => setPort(e.target.value)}
              inputMode="numeric"
              error={fieldErrors.port}
            />
            <Field
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              error={fieldErrors.username}
            />
            <Field
              label="Password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setClearPassword(false);
              }}
              autoComplete="new-password"
              placeholder={smtp?.password_set ? "•••••••• (unchanged)" : ""}
              help={passwordHelp}
            />
            <Field
              label="Password secret ref"
              className="mono"
              value={secretRef}
              onChange={(e) => setSecretRef(e.target.value.toUpperCase())}
              placeholder="GRACKLE_SMTP_PASSWORD"
              help="Optional env-var name. Wins over a stored password at send time."
              error={fieldErrors.secretRef}
            />
            <Field
              label="From address"
              type="email"
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
              placeholder="no-reply@grackle.example.com"
              error={fieldErrors.fromAddress}
              required
            />
            <Field label="From name" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Grackle" />
            <Field
              label="Reply-to (optional)"
              type="email"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              placeholder="support@grackle.example.com"
              help="Where replies go. Leave blank to omit the header."
              error={fieldErrors.replyTo}
            />
          </div>

          {smtp?.password_set ? (
            <label className="cluster">
              <input
                type="checkbox"
                checked={clearPassword}
                onChange={(e) => {
                  setClearPassword(e.target.checked);
                  if (e.target.checked) setPassword("");
                }}
              />
              Clear the stored password
            </label>
          ) : null}

          {smtpError ? <Notice tone="error">{smtpError}</Notice> : null}
          {smtpSaved ? <Notice>SMTP settings saved. The password is never shown again.</Notice> : null}

          <div className="cluster">
            <Button type="submit" loading={savingSmtp}>
              Save SMTP settings
            </Button>
            {smtp?.configured ? (
              <Button type="button" variant="ghost" disabled={savingSmtp} onClick={onClearSmtp}>
                Clear
              </Button>
            ) : null}
          </div>
        </form>
      </Card>

      <Card size="sm" title="Send test email">
        <p className="gk-sm ops-card-note">
          Sends through the saved transport. Leave the template blank to send a short SMTP probe.
        </p>
        {dirty ? (
          <Notice>You have unsaved SMTP changes. Save first — the test uses the saved settings, not this form.</Notice>
        ) : null}
        <form onSubmit={onTestSend} className="ops-form" noValidate>
          <Field
            label="Send to"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            required
          />
          <div className="gk-field">
            <Select
              id="ops-test-tpl"
              label="Template"
              value={testKey}
              onChange={(v) => setTestKey(v as EmailTemplateKey | "")}
              options={[{ value: "", label: "SMTP probe (no template)" }, ...(templates ?? []).map((t) => ({ value: t.key, label: t.name }))]}
            />
            <div className="gk-field__help">Sample variables are substituted for a template test.</div>
          </div>
          <Button type="submit" loading={testing} disabled={!testTo.trim()}>
            Send test email
          </Button>
        </form>
        {testError ? <Notice tone="error">{testError}</Notice> : null}
        {testResult ? <Notice>{testResult}</Notice> : null}
      </Card>

      <Card size="sm" title="Templates">
        <p className="gk-sm ops-card-note">Subject and body (HTML + plain). Variables are documented per template and substituted as {"{{name}}"}.</p>

        <Tabs
          variant="pill"
          className="ops-tpl-tabs"
          items={(templates ?? []).map((t) => ({ value: t.key, label: t.name }))}
          value={activeKey}
          onChange={selectTemplate}
        />
        <div className="ops-form">
          {active ? (
            <form onSubmit={onSaveTemplate} className="ops-form">
              <p className="gk-sm">{active.description}</p>
              <div className="cluster">
                {active.variables.map((v) => (
                  <Badge key={v.name} size="sm" title={v.description}>
                    <span className="mono">{`{{${v.name}}}`}</span>
                  </Badge>
                ))}
              </div>

              <Field label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} required />

              <div className="gk-field">
                <label className="gk-field__label" htmlFor="ops-tpl-html">
                  HTML body
                </label>
                <textarea
                  id="ops-tpl-html"
                  className="admin-textarea mono"
                  rows={10}
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                />
                <div className="gk-field__help">Values are HTML-escaped at send time. Markup in the template is not.</div>
              </div>

              <div className="gk-field">
                <label className="gk-field__label" htmlFor="ops-tpl-plain">
                  Plain body
                </label>
                <textarea
                  id="ops-tpl-plain"
                  className="admin-textarea"
                  rows={8}
                  value={bodyPlain}
                  onChange={(e) => setBodyPlain(e.target.value)}
                />
              </div>

              <Card size="sm" tone="sunken" title="Preview (sample values)">
                <b>{previewSubject}</b>
                <pre className="ops-preview">{previewPlain}</pre>
              </Card>

              {tplError ? <Notice tone="error">{tplError}</Notice> : null}
              {tplSaved ? <Notice>Template saved.</Notice> : null}

              <div className="cluster">
                <Button type="submit" loading={savingTpl} disabled={!subject.trim()}>
                  Save template
                </Button>
                <Button type="button" variant="secondary" disabled={savingTpl} onClick={onResetTemplate}>
                  Reset to default
                </Button>
              </div>
            </form>
          ) : (
            <div className="gk-sm">Loading templates…</div>
          )}
        </div>
      </Card>
    </>
  );
}
